import { FC, useEffect } from 'react';

import { differenceInDays, isValid as isDateValid, parseISO } from 'date-fns';
import { useHistory, useLocation } from 'react-router';
import {
    BALANCE_REPLENISHMENT_TRIGGER_PARAM,
    deleteBalanceReplenishmentParamFromUrl,
} from 'utils/balance/balanceReplenishment';
import useDefaultAmountForReplenishment from 'utils/balance/useDefaultAmountForReplenishment';
import { formatCost } from 'utils/clickme';
import { NON_BREAKING_SPACE } from 'utils/constants/common';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import clickmePaymentButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/invoice_purchase/clickme_payment_alert_button_click';
import clickmePaymentAlertElementShown from '@hh.ru/analytics-js-events/build/clickme/employer/invoice_purchase/clickme_payment_alert_element_shown';
import { Alert, Button, ButtonStack } from '@hh.ru/magritte-ui';
import { ExclamationTriangleFilledSize24 } from '@hh.ru/magritte-ui/icon';

import localStorageWrapper from 'bloko/common/storage/LocalStorageWrapper';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import withErrorBoundary from 'components/common/ErrorBoundary/withErrorBoundary';

import { selectNeedTopUp } from 'store/models/stoppedCampaigns';
import { useSelector } from 'store/utils';

const TrlKeys = {
    title: 'Dashboard.NeedToReplenishAlert.title',
    description: 'Dashboard.NeedToReplenishAlert.description.balance',
    description2: 'Dashboard.NeedToReplenishAlert.description.reservedBalance',
    confirmButton: 'Dashboard.NeedToReplenishAlert.description.confirmButton',
    cancelButton: 'Dashboard.NeedToReplenishAlert.description.cancelButton',
};

const BALANCE_REPLENISHMENT_LAST_SHOWN_DATE = 'BALANCE_REPLENISHMENT_LAST_SHOWN_DATE';
const BALANCE_REPLENISHMENT_MODAL_SHOW_PERIOD_IN_DAYS = 7;

const NeedToReplenishAlert: FC<{ openBalanceReplenishmentModal: () => void }> = ({ openBalanceReplenishmentModal }) => {
    const needTopUp = useSelector(selectNeedTopUp);
    const realBalance = useSelector((state) => state.balance?.realBalance);
    const reservedBalance = useSelector((state) => state.balance?.reservedBalance);

    const [isAlertVisible, setIsAlertVisibleOn, setIsAlertVisibleOff] = useOnOffState(false);

    const defaultAmountForReplenishment = useDefaultAmountForReplenishment();

    const trls = useTrls();
    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
        if (!needTopUp) {
            deleteBalanceReplenishmentParamFromUrl(history);
            return;
        }

        // проверяем, нужно ли показывать модальное окно по дате
        const lastShownDateString = localStorageWrapper.getItem(BALANCE_REPLENISHMENT_LAST_SHOWN_DATE) || '';
        let shouldShowByPeriod = false;

        const lastShownDate = parseISO(lastShownDateString);
        if (isDateValid(lastShownDate)) {
            const daysSinceLastShown = differenceInDays(new Date(), lastShownDate);
            shouldShowByPeriod = daysSinceLastShown >= BALANCE_REPLENISHMENT_MODAL_SHOW_PERIOD_IN_DAYS;
        } else {
            shouldShowByPeriod = true;
        }

        // проверяем, нужно ли показывать модальное окно по параметру в url
        const searchParams = new URLSearchParams(location.search);
        const shouldShowByUrlParam = searchParams.has(BALANCE_REPLENISHMENT_TRIGGER_PARAM);

        const shouldShowModal = shouldShowByPeriod || shouldShowByUrlParam;

        if (shouldShowModal) {
            setIsAlertVisibleOn();
            localStorageWrapper.setItem(BALANCE_REPLENISHMENT_LAST_SHOWN_DATE, new Date().toISOString());
        }
        deleteBalanceReplenishmentParamFromUrl(history);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needTopUp, location.search]);

    const onClose = () => {
        setIsAlertVisibleOff();
        clickmePaymentButtonClick({ buttonName: 'clickme_payment_alert_cancel' });
    };

    const onConfirm = () => {
        openBalanceReplenishmentModal();
        setIsAlertVisibleOff();
        clickmePaymentButtonClick({ buttonName: 'clickme_payment_alert_confirm' });
    };

    return (
        <Alert
            title={trls(TrlKeys.title, [formatCost(defaultAmountForReplenishment, NON_BREAKING_SPACE)])}
            description={
                <ElementShownAnchor fn={clickmePaymentAlertElementShown}>
                    <div>{trls(TrlKeys.description, [formatCost(realBalance, NON_BREAKING_SPACE)])}</div>
                    <div>{trls(TrlKeys.description2, [formatCost(reservedBalance, NON_BREAKING_SPACE)])}</div>
                </ElementShownAnchor>
            }
            buttons={
                <ButtonStack stretched layout="horizontal">
                    <Button
                        data-qa="need-to-replenish-alert-cancel-button"
                        onClick={onClose}
                        mode="secondary"
                        style="accent"
                        stretched
                    >
                        {trls(TrlKeys.cancelButton)}
                    </Button>
                    <Button
                        data-qa="need-to-replenish-alert-confirm-button"
                        mode="primary"
                        style="accent"
                        onClick={onConfirm}
                        stretched
                    >
                        {trls(TrlKeys.confirmButton)}
                    </Button>
                </ButtonStack>
            }
            visible={isAlertVisible}
            onClose={onClose}
            data-qa="need-to-replenish-alert"
        >
            <ExclamationTriangleFilledSize24
                initialColor="warning"
                backgroundStyle="warning-secondary"
                padding={12}
                borderRadius="halfHeight"
            />
        </Alert>
    );
};

export default withErrorBoundary(NeedToReplenishAlert);
